import {
  GeofenceEvent,
  useCreateGeofenceEventMutation,
  useDeleteGeofenceEventMutation,
  useUpdateGeofenceEventMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseGeofenceEventMutationProps = {}

export type UseGeofenceEventMutationResult = {
  createGeofenceEvent: (geofenceEvent: Partial<GeofenceEvent>, onSuccess: (() => void) | undefined) => void
  updateGeofenceEvent: (geofenceEvent: Partial<GeofenceEvent>, onSuccess: (() => void) | undefined) => void
  deleteGeofenceEvent: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useGeofenceEventMutation = (props: UseGeofenceEventMutationProps = {}): UseGeofenceEventMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createGeofenceEvent, {isLoading: isCreating}] = useCreateGeofenceEventMutation()
  const [_updateGeofenceEvent, {isLoading: isUpdating}] = useUpdateGeofenceEventMutation()
  const [_deleteGeofenceEvent, {isLoading: isDeleting}] = useDeleteGeofenceEventMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useGeofenceEventUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.geofenceevent", "Geofence Event"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useGeofenceEventUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useGeofenceEventUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.geofenceevent", "Geofence Event"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useGeofenceEventUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useGeofenceEventUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.geofenceevent", "Geofence Event"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useGeofenceEventUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofenceevent", "Geofence Event") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofenceevent", "Geofence Event") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofenceevent", "Geofence Event") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteGeofenceEvent({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteGeofenceEvent, handleDeleteResult])

  const createGeofenceEvent = useCallback((geofenceEvent: Partial<GeofenceEvent>, onSuccess: (() => void) | undefined) => {
    const promise = _createGeofenceEvent(geofenceEvent).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createGeofenceEvent, handleCreateResult])

  const updateGeofenceEvent = useCallback((geofenceEvent: Partial<GeofenceEvent>, onSuccess: (() => void) | undefined) => {
    const promise = _updateGeofenceEvent(geofenceEvent).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateGeofenceEvent, handleUpdateResult])

  const deleteGeofenceEvent = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.geofenceevent", "Geofence Event"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletegeofenceevent.confirm", "Are you sure you want to delete this geofence event?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createGeofenceEvent: createGeofenceEvent,
    updateGeofenceEvent: updateGeofenceEvent,
    deleteGeofenceEvent: deleteGeofenceEvent,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
